import React, { useCallback, useRef } from "react";
import sortBy from "lodash/sortBy";
import { useQuery } from "@apollo/client/react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";

import { GET_LOCATIONS_QUERY } from "../../../constants/graphql/queries";
import { filterOptions } from "../Cart/InventoryItemAddress";

export const initialValues = { location: "", building: "", room: "", poNumber: "" };

const isLocationOptionEqualToValue = (option, value) => {
  if (!value) return false;
  if (typeof value === "string") {
    return option.value === value || option.label === value;
  }
  if (value.inputValue) {
    return option.value === value.inputValue || option.label === value.label;
  }

  return option.value === value.value;
};

export default function DefaultItems({ values, onChange }) {
  const { data: { locations = [] } = {}, loading } = useQuery(GET_LOCATIONS_QUERY);
  const buildingRef = useRef();
  const roomRef = useRef();
  const poNumberRef = useRef();
  const options = sortBy(locations.map(v => ({ value: `${v.name}, ${v.address}`, label: v.name })));

  const onOptionSelected = option => {
    if (typeof option === "string") {
      const selected = options.find(v => v.value === option);
      return selected ? selected.label : option;
    }

    if (option.inputValue) {
      return option.label;
    }

    return option ? option.label : "";
  };

  const handleChangeField = useCallback(
    name => e => {
      onChange(v => ({ ...v, [name]: e.target.value }));
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange(initialValues);
    buildingRef.current.value = "";
    roomRef.current.value = "";
    poNumberRef.current.value = "";
  }, [onChange]);

  return (
    <Box position="relative">
      <Card>
        <Box p={1.5} height={68.5} display="flex" alignItems="center">
          <Grid container spacing={1}>
            <Grid size={4}>
              <Autocomplete
                size="small"
                freeSolo
                filterOptions={filterOptions}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                isOptionEqualToValue={isLocationOptionEqualToValue}
                getOptionLabel={onOptionSelected}
                onChange={(e, value) => {
                  const location = value?.inputValue || value?.value || "";
                  const [, street = "", city = "", zipCode = ""] = location.split(",");
                  const code = zipCode.trim().split(" ");
                  onChange(v => ({
                    ...v,
                    street: street.trim(),
                    city: city.trim(),
                    zip: code[code.length - 1],
                    location: value?.value || "",
                  }));
                }}
                options={options}
                value={values.location}
                renderInput={inputProps => (
                  <TextField {...inputProps} label="Location" variant="outlined" margin="none" />
                )}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                inputRef={buildingRef}
                label="Building"
                placeholder="Building"
                defaultValue={values.building}
                size="small"
                onBlur={handleChangeField("building")}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                inputRef={roomRef}
                label="Room"
                placeholder="Room"
                defaultValue={values.room}
                size="small"
                onBlur={handleChangeField("room")}
              />
            </Grid>
            {/* 
              <Grid item xs={3}>
               <TextField
                 inputRef={poNumberRef}
                 label="PO Number"
                 placeholder="PO Number"
                 defaultValue={values.poNumber}
                 size="small"
                 onBlur={handleChangeField("poNumber")}
               />
              </Grid>
              */}
          </Grid>
        </Box>
      </Card>
      {(values.building || values.room || values.poNumber || values.location) && (
        <Box position="absolute" right={-24} top={-24}>
          <IconButton onClick={handleClear}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
